/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceAccumulation.module.scss';

type Translate = (key: string) => string;

type Node = {
  x: number;
  y: number;
  root?: boolean;
  flag: string;
  name: string;
  source: string;
  label: string;
  limit?: string;
};

const getNodes = (t: Translate): Node[] => [
  {
    x: 345,
    y: 8,
    root: true,
    flag: '🇱🇺',
    name: 'MERIDIAN HOLDINGS S.à r.l.',
    source: 'Luxembourg RCS · filed 14 Apr 2026',
    label: t('ULTIMATE PARENT'),
  },
  {
    x: 135,
    y: 148,
    flag: '🇬🇧',
    name: 'ACME LOGISTICS HOLDINGS LTD',
    source: 'Companies House · PSC 11 Mar 2026',
    label: t('MOTOR FLEET'),
    limit: '£3.10m',
  },
  {
    x: 555,
    y: 148,
    flag: '🇳🇱',
    name: 'NORDHOLM FREIGHT B.V.',
    source: 'KVK Handelsregister · UBO register',
    label: t('MARINE CARGO'),
    limit: '£4.40m',
  },
  {
    x: 0,
    y: 288,
    flag: '🇬🇧',
    name: 'ACME COLD CHAIN LTD',
    source: 'Companies House · group accounts 2025',
    label: t('PROPERTY'),
    limit: '£6.20m',
  },
  {
    x: 270,
    y: 288,
    flag: '🇫🇷',
    name: 'ACME LOGISTIQUE SAS',
    source: 'Infogreffe · bénéficiaires effectifs',
    label: t('LIABILITY'),
    limit: '£4.90m',
  },
];

const EDGES = [
  { len: 274, d: 'M470 84 V116 H260 V148', pct: '82%', px: 365, py: 112 },
  { len: 274, d: 'M470 84 V116 H680 V148', pct: '61%', px: 575, py: 112 },
  { len: 199, d: 'M260 224 V256 H125 V288', pct: '100%', px: 192, py: 252 },
  { len: 199, d: 'M260 224 V256 H395 V288', pct: '100%', px: 327, py: 252 },
];

const NODE_EDGES = [[0, 1], [0, 2, 3], [1], [2], [3]];

const STEPS: { nodes?: number[]; edges?: number[]; addLimit?: number }[] = [
  { nodes: [0] },
  { edges: [0] },
  { nodes: [1], addLimit: 3_100_000 },
  { edges: [1] },
  { nodes: [2], addLimit: 4_400_000 },
  { edges: [2, 3] },
  { nodes: [3], addLimit: 6_200_000 },
  { nodes: [4], addLimit: 4_900_000 },
];

const STEP_MS = 320;
const STEP_START_MS = 180;
const COUNT_MS = 620;

export const InsuranceAccumulation = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.14 });
  const nodes = getNodes(t);
  const [nodesOn, setNodesOn] = useState(() => getNodes((k) => k).map(() => false));
  const [edgesOn, setEdgesOn] = useState(() => EDGES.map(() => false));
  const [agg, setAgg] = useState(0);
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);
  const aggFrom = useRef(0);
  const aggTo = useRef(0);
  const rafId = useRef<number>(undefined);

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const timers = STEPS.map((step, i) =>
      setTimeout(
        () => {
          if (step.nodes) {
            setNodesOn((prev) => {
              const next = [...prev];
              step.nodes?.forEach((n) => {
                next[n] = true;
              });
              return next;
            });
          }

          if (step.edges) {
            setEdgesOn((prev) => {
              const next = [...prev];
              step.edges?.forEach((e) => {
                next[e] = true;
              });
              return next;
            });
          }

          if (step.addLimit) {
            aggFrom.current = aggTo.current;
            aggTo.current += step.addLimit;
            const from = aggFrom.current;
            const to = aggTo.current;
            let t0 = 0;

            const tick = (ts: number) => {
              if (!t0) {
                t0 = ts;
              }

              const p = Math.min(1, (ts - t0) / COUNT_MS);
              const eased = 1 - (1 - p) ** 3;
              setAgg(from + (to - from) * eased);

              if (p < 1) {
                rafId.current = requestAnimationFrame(tick);
              }
            };

            rafId.current = requestAnimationFrame(tick);
          }
        },
        STEP_START_MS + i * STEP_MS,
      ),
    );

    return () => {
      timers.forEach(clearTimeout);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [revealed]);

  return (
    <Section background="#042354" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          theme="dark"
          title={t('One insured. Four policies. Priced as four unrelated risks.')}
          description={t(
            'Ownership resolved from share registers, beneficial-ownership filings and group returns in every jurisdiction the group files in. The link is read from the filing and dated. It is not modelled and not inferred.',
          )}
        />
      </div>

      <div className={styles.split} ref={ref}>
        <div className={styles.aggCol}>
          <span className={styles.agg}>
            <i>£</i>
            {(agg / 1_000_000).toFixed(2)}m
          </span>
          <div className={styles.aggLabel}>{t('Aggregate limit behind one ultimate parent')}</div>
          <Button type="white" size="xl" href="/book-a-demo" className={styles.aggCta}>
            {t('Run your book against it')}
          </Button>
        </div>

        <svg
          className={styles.graph}
          viewBox="0 0 810 375"
          role="img"
          aria-label={t(
            'Sample corporate group: Meridian Holdings holds Acme Logistics Holdings at 82 percent and Nordholm Freight at 61 percent; Acme Logistics Holdings wholly owns Acme Cold Chain and Acme Logistique.',
          )}
        >
          <defs>
            <linearGradient id="insAccGradChild" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,.08)" />
              <stop offset="100%" stopColor="rgba(255,255,255,.02)" />
            </linearGradient>
            <linearGradient id="insAccGradRoot" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(51,92,244,.26)" />
              <stop offset="100%" stopColor="rgba(51,92,244,.08)" />
            </linearGradient>
          </defs>

          {EDGES.map((edge, i) => (
            <path
              key={edge.d}
              className={cn(
                styles.ge,
                edgesOn[i] && styles.on,
                hoveredNode !== null && NODE_EDGES[hoveredNode].includes(i) && styles.hot,
              )}
              style={{ '--len': edge.len } as CSSProperties}
              d={edge.d}
            />
          ))}
          {EDGES.map((edge) => (
            <text key={edge.pct + edge.px} className={styles.gpct} x={edge.px} y={edge.py}>
              {edge.pct}
            </text>
          ))}
          {nodes.map((node, i) => (
            <g
              key={node.name}
              className={cn(styles.gn, nodesOn[i] && styles.on, hoveredNode === i && styles.hovered)}
              transform={`translate(${node.x},${node.y})`}
              onMouseEnter={() => setHoveredNode(i)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <title>{node.name}</title>
              <rect
                className={cn(styles.gbox, node.root && styles.gboxR)}
                width="250"
                height="76"
                rx="12"
                fill={node.root ? 'url(#insAccGradRoot)' : 'url(#insAccGradChild)'}
              />
              <circle className={cn(styles.gflagBadge, node.root && styles.gflagBadgeR)} cx="21" cy="20" r="11" />
              <text className={styles.gflag} x="21" y="24">
                {node.flag}
              </text>
              <text className={cn(styles.gname, node.root && styles.gnameR)} x="42" y="24">
                {node.name}
              </text>
              <text className={styles.gsrc} x="12" y="42">
                {node.source}
              </text>
              <line className={styles.gdiv} x1="12" y1="52" x2="238" y2="52" />
              {node.root ? (
                <text className={cn(styles.glab, styles.glabR)} x="12" y="67">
                  {node.label}
                </text>
              ) : (
                <>
                  <text className={styles.glab} x="12" y="65">
                    {node.label}
                  </text>
                  <text className={styles.glim} x="238" y="68">
                    {node.limit}
                  </text>
                </>
              )}
            </g>
          ))}
        </svg>
      </div>
    </Section>
  );
};
